/**
 * MODULE: domain/pages/frontend/utils/pageTocUtils.ts
 * PURPOSE: Extract headings from a Pages note editor for table-of-contents blocks
 */

import type { Editor } from '@tiptap/react';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

export type TocHeading = {
  blockId: string | null;
  pos: number;
  level: 1 | 2 | 3;
  text: string;
};

export type TocMaxDepth = 1 | 2 | 3;

export const DEFAULT_TOC_TITLE = 'On this page';

export function normalizeTocMaxDepth(value: unknown): TocMaxDepth {
  const depth = Number(value);
  if (depth === 1 || depth === 2) return depth;
  return 3;
}

export function extractHeadingsFromDoc(doc: ProseMirrorNode): TocHeading[] {
  const headings: TocHeading[] = [];

  doc.descendants((node, pos) => {
    if (node.type.name !== 'heading') {
      return true;
    }

    const level = Number(node.attrs.level ?? 1);
    const normalizedLevel = level === 2 ? 2 : level === 3 ? 3 : 1;
    const blockId =
      typeof node.attrs.pagesBlockId === 'string' && node.attrs.pagesBlockId.length > 0
        ? node.attrs.pagesBlockId
        : null;
    const text = node.textContent.trim() || 'Untitled section';

    headings.push({
      blockId,
      pos,
      level: normalizedLevel,
      text,
    });
    return true;
  });

  return headings;
}

export function extractHeadingsFromEditor(editor: Editor | null): TocHeading[] {
  if (!editor || editor.isDestroyed) {
    return [];
  }
  return extractHeadingsFromDoc(editor.state.doc);
}

export function filterHeadingsByDepth(headings: TocHeading[], maxDepth: TocMaxDepth): TocHeading[] {
  return headings.filter((heading) => heading.level <= maxDepth);
}

export function tocHeadingIndentClass(level: TocHeading['level']): string {
  if (level === 2) return 'pl-4';
  if (level === 3) return 'pl-8';
  return 'pl-0';
}

export function scrollToTocHeading(editor: Editor, heading: TocHeading): void {
  if (heading.blockId) {
    const anchor = document.querySelector(`[data-pages-block-id="${heading.blockId}"]`);
    if (anchor) {
      anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
  }

  editor.chain().focus().setTextSelection(heading.pos + 1).scrollIntoView().run();
}
